/**
 * Service calls within nested UIs need some kind of extra processing to get them to their destination in one piece
 *
 * Each service url in urls.js will be updated by each function in this array
 * Paymode will add functions to it before loading any other Connect code,
 * so urls can trust that its urls are correct from load time
 * @type {Array}
 */
const serviceUrlProcessors = [];

/**
 * For integrations routes need somewhat similar processing to
 * urls to ensure they mess with the routes of the parent app.
 * We store the processors for routes here.
 *
 * @type {Array}
 */
const routeUrlProcessors = [];

/**
 * Stores filters that operate on collections of Apps,
 * determining which ones should be stripped out to hide them from the UI.
 * @type {Array}
 */
const appsFilters = [];

/**
 * A bag of arrays of functions for updating views once they've been shown from integrated projects
 * @type {{}}
 */
const viewPostProcessors = {};

/**
 * A bag of arrays of functions for modifying grid columns from integrated projects
 * @type {{}}
 */
const gridColumnProcessors = {};

/**
 * A hash of functions for specifying defaults on named models
 * @type {{}}
 */
const modelDefaults = {};

/**
 * A hash of functions for customizing jquery selections prior to creating a combobox on them.
 * @type {{}}
 */
const comboBoxCustomizations = {};

let pmx = false;

let disableAnalytics = false;

let defaultCurrency;

/**
 * Process a single url through an array of functions that process it as needed and return the processed url.
 *
 * @param url Url to process
 * @param processors Array of functions that will process the url as needed.
 */
function processUrl(url, processors) {
  return processors.reduce((memo, processor) => processor(memo), url);
}

/**
 * Process a url key/value pair where the url can be in either the key or the value.
 * The url is processed through all of the provided processors and returned in a key/value pair hash.
 * The non-url key or value is simply passed through unchanged.
 *
 * @param key key to process
 * @param value value to process, assumed to be the url unless urlIsInKey is true.
 * @param processors Array of functions that will process the url as needed.
 * @param urlIsInKey True if the key is the url, false if the value is the url
 * @returns {{key: *, value: *}}
 */
function processUrlKeyValue(key, value, processors, urlIsInKey) {
  const processedUrl = processUrl(urlIsInKey ? key : value, processors);

  return {
    key: urlIsInKey ? processedUrl : key,
    value: urlIsInKey ? value : processedUrl
  };
}

/**
 * Process a hash of urls where the url can be in either the key or the value portion of the hash.
 *
 * @param urlHash Hash of urls
 * @param processors Array of functions that will process each entry in the hash
 * @param urlIsInKey  Boolean indicating if the url is in the key part of the hash,
 *                    otherwise we assume its in the value(default)
 * @returns {*}
 */
function processUrlHash(urlHash, processors, urlIsInKey) {
  if (!processors.length) {
    return urlHash;
  }

  return Object.keys(urlHash).reduce((newUrls, key) => {
    const newKeyValue = processUrlKeyValue(key, urlHash[key], processors, urlIsInKey);

    newUrls[newKeyValue.key] = newKeyValue.value; // eslint-disable-line

    return newUrls;
  }, {});
}

/**
 * This is a utility class for managing UI integration points between Glu apps
 *
 * The idea here is to make integration points robust and stable.
 * There could be multiple levels (e.g. Connect in Paymode in Banking)
 *  and we want them to interoperate cleanly.
 */
export default {
  /**
     * Returns the passed in urls with all service url processors applied to each
     *
     * @param urls {object} A hash of service urls
     * @returns {object}
     */
  applyServiceUrlProcessors(urls) {
    return processUrlHash(urls, serviceUrlProcessors, false);
  },

  /**
     * Adds a new function to the list of service url processors
     * @param processor
     */
  appendServiceUrlProcessor(processor) {
    serviceUrlProcessors.push(processor);
  },

  /**
     * Returns the passed in urls with all route url processors applied to each
     *
     * @param routeUrls {object} A hash of route urls
     * @param urlIsInKey  {boolean} True if the url is in the key instead of the value,
     *                    if falsey we assume the url is in the value portion of the hash.
     * @returns {object}
     */
  applyRouteUrlProcessors(routeUrls, urlIsInKey) {
    return processUrlHash(routeUrls, routeUrlProcessors, urlIsInKey);
  },

  /**
     * Adds a new function to the list of route url processors
     * @param processor
     */
  appendRouteUrlProcessor(processor) {
    routeUrlProcessors.push(processor);
  },

  getRoutesRoot() {
    return this.routesRoot;
  },

  setRoutesRoot(root) {
    this.routesRoot = root;
  },

  isPMX() {
    return pmx;
  },

  setPMX(value) {
    pmx = !!value;
  },

  /**
     * Should PIWIK Analytics logging be disabled?
     *
     * @returns {boolean|*} True if analytics should be disabled, false otherwise.
     */
  isDisableAnalytics() {
    return disableAnalytics;
  },

  /**
     * Should PIWIK Analytics logging be disabled?
     *
     * @param disable {boolean} True if analytics should be disabled, false otherwise.
     */
  setDisableAnalytics(disable) {
    disableAnalytics = !!disable;
  },

  /**
     * Filter an array of Apps to remove any that should not be visible to the UI in an a given integration.
     *
     * @param apps Apps to filter
     * @returns {*} Filtered Apps
     */
  applyAppsFilters(apps) {
    if (!appsFilters.length) {
      return apps;
    }

    return apps.filter((app, index, array) => appsFilters.reduce((memo, filter) => {
      // once one filter rejects the app then there is no need to keep testing
      if (memo) {
        memo = filter(app, index, array); // eslint-disable-line
      }

      return memo;
    }, true));
  },

  /**
     * Add a filter to the list of App filters
     *
     * @param filter A javascript Array filter function: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/filter
     */
  appendAppsFilter(filter) {
    appsFilters.push(filter);
  },

  /**
     * Adds a new function for modifying a specific view
     * @param name A chosen name for the view
     * @param callback A function that operates on the view
     */
  appendViewPostProcessor(name, callback) {
    viewPostProcessors[name] = viewPostProcessors[name] || [];
    viewPostProcessors[name].push(callback);
  },

  /**
     * Executes all functions defined for a given view
     * @param name A chosen name for the view
     * @param view
     */
  applyViewPostProcessors(name, view) {
    const postProcessors = viewPostProcessors[name] || [];
    postProcessors.forEach((callback) => {
      callback(view);
    });
  },

  /**
     * Adds a new function for modifying a specific grid's columns
     *
     * @param id The grid id
     * @param callback A function that returns the new version of the grid columns
     */
  appendGridColumnProcessor(id, callback) {
    gridColumnProcessors[id] = gridColumnProcessors[id] || [];
    gridColumnProcessors[id].push(callback);
  },

  /**
     * Applies all functions defined for a given grid id to the grid columns
     *
     * @param id The grid id
     * @param gridColumns The original grid columns
     * @returns {*} The new version of grid columns
     */
  applyGridColumnProcessors(id, gridColumns) {
    const columnProcessors = gridColumnProcessors[id] || [];
    return columnProcessors.reduce((memo, callback) => callback(gridColumns), gridColumns);
  },

  getDefaultCurrency() {
    return defaultCurrency;
  },

  setDefaultCurrency(currencyCode) {
    defaultCurrency = currencyCode;
  },

  /**
     * Customize the defaults for a named model for an integration.
     *
     * @param defaults Current defaults for the model
     * @param modelName Name of the model whose defaults we are customizing
     * @returns {*}
     */
  applyModelDefaultsCustomization(modelName, defaults) {
    const customizer = modelDefaults[modelName];

    return customizer ? customizer(defaults) : defaults;
  },

  /**
     * Set a customization function that will customize the defaults for a named model for an integration.
     *
     * @param customizer  Function that will accept the original defaults for a model and then
     *                    return a version of those defaults customized for an integration.
     * @param modelName The name of the model we are setting the defaults customization for.
     */
  setModelDefaultsCustomization(modelName, customizer) {
    modelDefaults[modelName] = customizer;
  },

  /**
     * Apply integration specific customizations to the html for a named combobox prior to its initialization.
     *
     * @param comboBoxName Name of the combobox to customize.
     * @param $comboBox jquery selector for the select element to be turned into a combobox
     */
  applyComboBoxCustomizations(comboBoxName, $comboBox) {
    const customizer = comboBoxCustomizations[comboBoxName];

    if (customizer) {
      customizer($comboBox);
    }
  },

  /**
     * Set an integration customization for a specific named combobox.
     *
     * @param comboBoxName name of the combobox to customize
     * @param customizer  function that will customize the html of the combobox
     *                    before it it initialized (for example to add or remove elements)
     */
  setComboBoxCustomization(comboBoxName, customizer) {
    comboBoxCustomizations[comboBoxName] = customizer;
  }
};
